import { hasPermission } from '../permissions.js';

/**
 * The Settings information architecture.
 *
 * ONE source of truth for the Settings centre: the sidebar, the Settings home
 * cards and the tests all read this map, so a section cannot appear in one
 * place and be missing from another.
 *
 * Two rules govern what is allowed in here.
 *
 *  1. GOVERNING PRINCIPLE — customers manage their organization; Phibian
 *     manages Phibian. Nothing here exposes infrastructure telemetry, queue
 *     internals, sync-engine mechanics, or database/config internals. Those are
 *     control-plane concerns and belong to Phibian, not to a customer's
 *     settings screen.
 *
 *  2. NO PLACEHOLDERS. Every entry below is backed by a real API surface that
 *     performs a real operation. A capability with no backend is NOT listed as
 *     a disabled item or a "coming soon" tile — it is absent, and recorded in
 *     UNSUPPORTED_CAPABILITIES so the omission is deliberate and reviewable
 *     rather than forgotten.
 *
 * This is application-wide product navigation. It is derived only from the
 * signed-in principal's permissions, so it behaves identically for every
 * organization — there is no tenant-specific branch anywhere in this file.
 */

export interface SettingsItem {
  key: string;
  label: string;
  /** One line explaining what a person can actually do here. */
  description: string;
  href: string;
  /** Permission required to READ the section. Absent → authenticated-only. */
  permission?: string;
  /** The API surface behind it — documentation, and a review aid. */
  backedBy: string;
}

export interface SettingsSection {
  key: string;
  label: string;
  description: string;
  items: SettingsItem[];
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    key: 'setup',
    label: 'Getting set up',
    description: 'The steps between a new Phibian organization and a live one.',
    items: [
      {
        key: 'onboarding',
        label: 'Getting set up',
        description:
          'The fourteen-step first run: what is recorded, what is outstanding, and what Phibian checks before recording it.',
        href: '/settings/onboarding',
        permission: 'onboarding:read',
        backedBy: 'GET /api/onboarding/status, POST /api/onboarding/complete/:step',
      },
    ],
  },
  {
    key: 'organization',
    label: 'Organization',
    description: 'Your organization’s identity, working week, and where your teams operate.',
    items: [
      {
        key: 'org',
        label: 'Organization profile',
        description: 'Name, contact details, timezone, working week, and escalation defaults.',
        href: '/settings/org',
        permission: 'org:manage',
        backedBy: 'GET/PUT /organization',
      },
      {
        key: 'programs',
        label: 'Programs',
        description:
          'The operational units your work belongs to — their people, sites, leads and lifecycle.',
        href: '/settings/programs',
        permission: 'program:read',
        backedBy: 'GET/POST/PATCH /programs, /programs/:id/workers, /programs/:id/sites',
      },
      {
        key: 'sites',
        label: 'Sites & locations',
        description:
          'The physical places you operate from: addresses, map positions, geofences and the programs that work there.',
        href: '/settings/sites',
        permission: 'location:read',
        backedBy: 'GET/POST/PATCH /sites, PUT /sites/:id/geofence, /sites/:id/programs',
      },
      {
        key: 'jurisdictions',
        label: 'Operating jurisdictions',
        description:
          'Where your organization operates — organization-wide or narrowed to a program, site or service, with the dates each one was in force. This is what decides which external requirements apply to your work.',
        href: '/settings/jurisdictions',
        permission: 'jurisdiction:configure',
        backedBy:
          'GET/POST /jurisdictions, POST /jurisdictions/:id/end, GET /jurisdictions/revisions, GET /jurisdictions/resolve',
      },
      {
        key: 'services',
        label: 'Services',
        description: 'The service types your programs deliver, and per-client service targets.',
        href: '/settings/services',
        permission: 'service:read',
        backedBy: 'GET/POST/PATCH /services/types, /services/targets',
      },
    ],
  },
  {
    key: 'people',
    label: 'People & access',
    description: 'Who can sign in, what they can do, and who they report to.',
    items: [
      {
        key: 'people',
        label: 'People & roles',
        description:
          'Invite people, assign roles and direct permissions, and manage account status.',
        href: '/settings/people',
        permission: 'user:read',
        backedBy: 'GET /users, /roles, /invitations; POST /users/:id/roles, /permissions',
      },
      {
        key: 'roles',
        label: 'Roles & permissions',
        description:
          'What each role can do, in plain language — compare roles, search the permission catalog, compose a role of your own, and move the superuser tier.',
        href: '/settings/roles',
        permission: 'user:read',
        backedBy:
          'GET /roles/overview; POST/PUT /orgchart/roles, /orgchart/roles/assign, /orgchart/superusers',
      },
      {
        key: 'intelligence',
        label: 'Intelligence',
        description:
          'How Phibian Intelligence carries out changes — plan, manual, auto or bypass — for the organization, each area, each role and each person. Bypass skips Intelligence’s confirmation only.',
        href: '/settings/intelligence',
        permission: 'org:manage',
        backedBy: 'GET/PUT /api/intelligence/settings',
      },
    ],
  },
  {
    key: 'workforce',
    label: 'Workforce',
    description: 'How time is recorded, where work happens, and how absence is handled.',
    items: [
      {
        key: 'location',
        label: 'Location & geofencing',
        description:
          'When location is collected, how it is checked, and how long it is kept — the policy hub.',
        href: '/settings/location',
        permission: 'location:read',
        backedBy:
          'GET /location/policy/effective, PUT /location/policy, GET/POST/PATCH /location/geofences (location:manage), GET /sites',
      },
      {
        key: 'timeoff',
        label: 'Time off',
        // TWO SENTENCES THAT WERE NOT TRUE ABOUT THIS SCREEN. It said requests
        // were reviewed here and claimed `GET /timeoff/requests` as its
        // backing; `TimeoffSettings.tsx` calls `timeoff.types` and
        // `timeoff.createType` and nothing else, and for a year there was no
        // reviewer surface anywhere in the product for the claim to be about.
        // The review queue now exists — `/time/approvals`, beside the
        // corrections queue — so this points at it instead of pretending to be
        // it. `backedBy` is documentation AND a review aid; a route named here
        // and called nowhere is the kind of claim this whole parity sweep
        // exists to find.
        description:
          'Time-off types and whether they are paid. Requests are reviewed on Time \u203a Approvals.',
        href: '/settings/timeoff',
        permission: 'timeoff:read',
        backedBy: 'GET/POST/PATCH /timeoff/types',
      },
    ],
  },
  {
    key: 'automation',
    label: 'Notifications & automations',
    description:
      'Operational rules that watch your data, and how their outcomes reach your people.',
    items: [
      {
        key: 'rules',
        label: 'Automations',
        description: 'Rules that raise attention items, who they notify, and when they escalate.',
        href: '/settings/rules',
        permission: 'rules:read',
        backedBy: 'GET /rules/automations, PATCH /rules/:id',
      },
      {
        /**
         * Rungs 1 and 4 of the escalation ladder (founder ruling 5,
         * 2026-09-07). `finding_escalation_target_config` and its two write
         * routes have existed since migration 1854000000000 with no client at
         * all: an organization could not name who its findings escalate to.
         * This is not the Automations "escalate after N minutes" control —
         * that is an alert rule's timer; this is WHO organizational authority
         * resolves to, and the two are different mechanisms with different
         * tables.
         */
        key: 'escalation-targets',
        label: 'Escalation targets',
        description:
          'Who a finding escalates to when nobody has been assigned it — per finding type or detector, and your organization’s fallback role.',
        href: '/settings/escalation-targets',
        permission: 'org:manage',
        backedBy:
          'GET/POST /api/insights/escalation-targets, POST /api/insights/escalation-targets/:id/retire',
      },
      {
        /**
         * Founder closeout decision 3 of 2026-09-08. `finding_attention_budget`
         * has existed since migration 2070000000000 with no client at all: an
         * organization could not say how much Phibian may interrupt one person,
         * and the number nobody could set was being clamped by a floor the
         * founder then declined to establish. Deliberately NOT under
         * `org:manage`, unlike Escalation targets above — the decision asks for
         * the SMALLEST key that opens this, and `org:manage` is also the
         * agency's name and timezone.
         */
        key: 'attention-budget',
        label: 'Attention budget',
        description:
          'How many finding interruptions one person may receive in one organization day. Opt-in: set nothing and nothing is held back — never a budget of zero.',
        href: '/settings/attention-budget',
        permission: 'insights:attention_budget',
        backedBy: 'GET/PUT /api/insights/attention-budget',
      },
      {
        key: 'notifications',
        label: 'Text messaging',
        description: 'Organization-wide SMS delivery for notifications, and its sending number.',
        href: '/settings/notifications',
        permission: 'sms:manage',
        backedBy: 'GET/PUT /api/admin/sms/config',
      },
    ],
  },
  {
    key: 'mileage',
    label: 'Mileage',
    description:
      'Whether drives are captured in the background, what is reimbursable, and at what rate.',
    items: [
      {
        key: 'mileage',
        label: 'Mileage policy & rates',
        description:
          'Turn capture and reimbursement on or off, set the minimum reimbursable distance, and manage effective-dated per-mile rates.',
        href: '/settings/mileage',
        permission: 'mileage:configure',
        backedBy: 'GET/PUT /mileage/policy, POST /mileage/rates',
      },
    ],
  },
  {
    /**
     * The Exports catalog's deeper-admin home (founder Phase A review §4):
     * Exports left the rail foot — the foot stays Integrations · Settings ·
     * Profile — and a route does not earn rail real estate just because it
     * exists. It is NOT a setting in the change-a-policy sense, but the
     * Settings centre is the ruled "deeper Admin destination", each hub will
     * grow its own export in place, and global Find still answers "export".
     */
    key: 'reporting',
    label: 'Data out',
    description: 'What leaves Phibian as a file, and for whom.',
    items: [
      {
        key: 'exports',
        label: 'Exports',
        description: 'Workforce, verification, exception, and payroll-period CSVs, in one catalog.',
        href: '/reporting/exports',
        permission: 'reporting:export',
        backedBy: 'GET /api/admin-views/export/*',
      },
    ],
  },
  {
    key: 'connections',
    label: 'Connections',
    description: 'The systems Phibian exchanges records with.',
    items: [
      {
        key: 'records',
        label: 'Integrations',
        description: 'Connect a system, see its current state, and manage its credentials.',
        href: '/settings/records',
        permission: 'integrations:read',
        backedBy: 'GET /integrations/connectors, POST /integrations/:id/*',
      },
    ],
  },
];

/**
 * Capabilities deliberately NOT in the Settings navigation, and why.
 *
 * This list is the honest counterpart to the map above: each entry is a
 * capability a mature settings centre might be expected to have, which Phibian
 * does not support today. Shipping a dead toggle for any of these would be
 * worse than their absence — it would imply an operational effect that does not
 * exist. They are recorded here so the gap is visible to review and to whoever
 * picks up the backend work, and so a future contributor does not assume the
 * omission was an oversight.
 */
/*
 * REMOVED, 2026-09-08 (lane UI-FINAL): "Mileage & reimbursement configuration
 * — no mileage domain exists; there is no trip, mileage or reimbursement table
 * anywhere in the schema."
 *
 * That sentence had been false for some time and it was sitting eight lines
 * below a shipped `/settings/mileage` entry in the same file. Ten
 * `mileage_*` tables exist, `GET/PUT /mileage/policy` and `POST /mileage/rates`
 * are live, and Mileage is a primary card in the Operations hub.
 *
 * WHY THE GUARD DID NOT CATCH IT, WHICH IS THE PART WORTH KEEPING. The case in
 * `settings-nav.test.ts` exists precisely to stop a capability being shipped
 * AND recorded as unsupported — but it asked whether the recorded capability
 * STARTS WITH the shipped label, and the two are worded differently ("Mileage
 * policy & rates" against "Mileage & reimbursement configuration"). A prefix
 * test over two independently authored phrasings can only match when somebody
 * happens to write them the same way. The case now compares on the first word
 * of each, which is what actually names the domain.
 */
export const UNSUPPORTED_CAPABILITIES: { capability: string; reason: string }[] = [
  {
    capability: 'Program-scoped administration (managing only your own programs)',
    reason:
      'A program:read grant CAN be scoped to specific programs, and the Programs API honours that on reads. program:manage is organization-wide only: scoping writes would imply the same protection across schedules, dashboards and reporting, which is not enforced there yet. Shipping half of it would be worse than shipping none.',
  },
  {
    capability: 'Notification digests and quiet hours',
    reason:
      'Per-user notification preferences now exist under My notifications, covering which categories reach your phone. Batching into a digest, and time-of-day quiet hours, have no delivery model behind them yet — they would be switches that changed nothing.',
  },
  {
    capability: 'Certification status (SOC 2, HITRUST, ISO 27001 badges or switches)',
    reason:
      'Phibian holds no such attestation, so a badge or a toggle would assert something untrue on the customer’s behalf. The Compliance area reports the controls an organization has configured and the evidence Phibian recorded, and states in plain words that this is not certification.',
  },
  {
    capability: 'Audit retention policy (a "keep records for N years" setting)',
    reason:
      'Activity records are kept indefinitely and no retention or deletion schedule exists for them. A configurable retention control would imply an enforcement mechanism that is not implemented; the Compliance area states the actual behaviour instead.',
  },
  {
    capability: 'Scheduling defaults beyond the working week',
    reason:
      'Week start and default weekly hours are organization settings and are exposed. Shift templates, rotation patterns and auto-assignment rules have no configuration model.',
  },
];

/** The Settings sections a principal may actually see. */
export function visibleSettings(
  permissions: readonly string[],
  sections: SettingsSection[] = SETTINGS_SECTIONS,
): SettingsSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.permission || hasPermission(permissions, item.permission),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

/** The settings item matching a pathname, if any (used to mark the sidebar). */
export function activeSettingsItem(pathname: string): SettingsItem | undefined {
  const all = SETTINGS_SECTIONS.flatMap((s) => s.items);
  // Longest href first so /settings/people/$id matches /settings/people.
  return [...all]
    .sort((a, b) => b.href.length - a.href.length)
    .find((item) => pathname === item.href || pathname.startsWith(`${item.href}/`));
}
